/**
 * Backdrop video that only runs while it is on screen.
 *
 * A looping `<video>` keeps decoding wherever the reader is on the page, and
 * the landing page has more than one. Playing each only while its section is
 * in view keeps a phone to one decode at a time, and — with `preload="none"` —
 * means a video far down the page is not downloaded until it is reached.
 */

import { useEffect, useRef, type RefObject } from 'react';

export function useInViewVideo(): RefObject<HTMLVideoElement> {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (typeof IntersectionObserver === 'undefined') {
      void video.play().catch(() => undefined);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        // A refused play (data saver, low-power mode) leaves the still behind it.
        if (entry.isIntersecting) void video.play().catch(() => undefined);
        else video.pause();
      },
      // Start a little early so it is already moving as it scrolls in.
      { rootMargin: '200px 0px' },
    );
    io.observe(video);
    return () => io.disconnect();
  }, []);

  return ref;
}
